import React from "react";
import { Loader, MessageSquare } from "lucide-react";
import { EXCEPTION_LABELS, SHEET_LABELS, fmtDate } from "../ExcelUploadManager";
import { ExcelStatusBadge, Tag } from "./badges";
import { hasAsn } from "./excelData";

const HEADERS = {
    inbound:  ["PO / Line / SKU","Container","Supplier","Origin","Destination","Carrier","Vessel","ETA","Status","Flags","Notes"],
    outbound: ["Connote / Shipment","Receiver","Suburb","State / Site","Type","PO","Req. Delivery","Status","Comments","Notes"],
};

// Plain text cells between the reference/container columns and the ETA
function middleCells(reportType, r) {
    const d = r.data || {};
    return reportType === "inbound"
        ? [r.party, d.place_of_receipt_port, [d.destination_city, d.plant_code && `Plant ${d.plant_code}`].filter(Boolean).join(" · "), d.carrier, d.vessel]
        : [r.party, d.suburb, <SiteCell d={d}/>, SHEET_LABELS[r.sheetCode], d.purchase_order_number];
}

// Excel sometimes stores a site code as a date (MAR40 → "Mar-40"): shown as-is, flagged as odd
function SiteCell({ d }) {
    const text = [d.receiving_state, d.delivery_site].filter(Boolean).join(" · ");
    if (!d.delivery_site_odd) return text || null;
    return (
        <span title={`Excel stored this site code as a date ("${d.delivery_site}") — check it with the carrier`}>
            {text} <span className="ml-1 px-1 py-px rounded bg-amber-500/15 text-amber-300 text-[9px] font-bold">ODD</span>
        </span>
    );
}

function Flags({ reportType, r }) {
    const s = r.schedule;
    const inbound = reportType === "inbound";
    return (
        <td className="px-4 py-3">
            <div className="flex flex-wrap gap-1">
                {r.delayed && <Tag tone="amber" title={s?.reason || ""}>Delayed</Tag>}
                {s?.bucket === "onTime" && <Tag tone="green">On time</Tag>}
                {s?.bucket === "noEta" && <Tag tone="slate" title={s.reason || ""}>{inbound ? "No ERD" : "No req. date"}</Tag>}
                {s?.met === true && <Tag tone="green">{inbound ? "Received by ERD" : "Delivered on time"}</Tag>}
                {s?.met === false && s.bucket !== "delayed" && <Tag tone="amber" title={s.reason || ""}>{inbound ? "Received late" : "Delivered late"}</Tag>}
                {r.exceptions.map(e => <Tag key={e} tone="red">{EXCEPTION_LABELS[e] || e}</Tag>)}
            </div>
            {r.delayed && s?.reason && (
                <p className="text-[10px] text-amber-400/80 mt-1 whitespace-nowrap">{s.reason}</p>
            )}
        </td>
    );
}

function Notes({ notes }) {
    return (
        <td className="px-4 py-3 max-w-[180px]">
            {notes ? (
                <div title={`${notes.last}\n— ${notes.lastBy || ""}`}>
                    <span className="inline-flex items-center gap-1 text-[10px] text-blue-300">
                        <MessageSquare size={11}/> {notes.count}
                    </span>
                    <p className="text-[10px] text-slate-400 truncate">{notes.last}</p>
                </div>
            ) : (
                <span className="text-[10px] text-slate-600">Add note</span>
            )}
        </td>
    );
}

function Row({ reportType, r, statusLabels, onOpen }) {
    const d = r.data || {};
    const dot = r.exceptions.length ? "#EF4444" : r.delayed ? "#F59E0B" : r.status === "DELIVERED" ? "#10B981" : "#3B82F6";
    return (
        <tr onClick={() => onOpen(r.id)} className="hover:bg-[#1F2937]/40 transition-colors cursor-pointer">
            <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{backgroundColor:dot}}/>
                    <span className="font-mono font-semibold text-slate-200 text-[11px] whitespace-nowrap">{r.ref}</span>
                </div>
                {d.description && (
                    <p className="text-[10px] text-slate-500 pl-4 max-w-[260px] truncate" title={d.description}>{d.description}</p>
                )}
            </td>
            {reportType === "inbound" && (
                <td className="px-4 py-3 whitespace-nowrap">
                    {d.equipment_number
                        ? <span className="font-mono text-[11px] text-slate-200">{d.equipment_number}</span>
                        : <span className="text-[10px] text-slate-600 italic">Not assigned</span>}
                </td>
            )}
            {middleCells(reportType, r).map((c, i) => (
                <td key={i} className="px-4 py-3 text-slate-400 max-w-[140px] truncate">{c || "—"}</td>
            ))}
            <td className={`px-4 py-3 whitespace-nowrap ${r.delayed ? "text-amber-400" : "text-slate-300"}`}>
                {fmtDate(r.eta)}
                {reportType === "inbound" && r.schedule?.target && (
                    <p className="text-[10px] text-slate-500">
                        ERD {fmtDate(r.schedule.target)}
                        {r.schedule.actual && <> · rcvd {fmtDate(r.schedule.actual)}</>}
                    </p>
                )}
            </td>
            <td className="px-4 py-3"><ExcelStatusBadge code={r.status} raw={r.statusRaw} labels={statusLabels}/></td>
            {reportType === "outbound" && (
                <td className={`px-4 py-3 max-w-[180px] truncate ${hasAsn(d.comments) ? "text-red-400 font-semibold" : "text-slate-400"}`} title={d.comments || ""}>
                    {d.comments || "—"}
                </td>
            )}
            {/* Flags column is inbound only (delay / receipt explanations) */}
            {reportType !== "outbound" && <Flags reportType={reportType} r={r}/>}
            <Notes notes={r.notes}/>
        </tr>
    );
}

// Rows of one Excel report (inbound PO lines or outbound consignments)
export default function ExcelShipmentRows({ reportType, rows, statusLabels, onOpenRow }) {
    const { rows: data, loading, filtersActive, resetFilters } = rows;
    return (
        <table className="w-full text-left text-xs">
            <thead className="sticky top-0 z-10 shadow-[0_1px_0_#1F2937]">
                <tr className="border-t border-[#1F2937] bg-[#0D1520]">
                    {HEADERS[reportType].map(h => (
                        <th key={h} className="px-4 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody className="divide-y divide-[#1F2937]">
                {loading && data.rows.length === 0 ? (
                    <tr><td colSpan={11} className="px-4 py-10 text-center"><Loader size={20} className="animate-spin mx-auto text-slate-700"/></td></tr>
                ) : data.rows.length === 0 ? (
                    <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-500 text-sm">
                        No matching records.
                        {filtersActive && <button onClick={resetFilters} className="ml-2 text-blue-400 hover:underline">Clear filters</button>}
                    </td></tr>
                ) : data.rows.map(r => (
                    <Row key={r.id} reportType={reportType} r={r} statusLabels={statusLabels} onOpen={onOpenRow}/>
                ))}
            </tbody>
        </table>
    );
}
